import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/features/auth/AuthContext";
import type { Profile } from "@/lib/database.types";

type SaveState = "idle" | "saving" | "saved" | "error";

/**
 * Local editable copy of the profile with debounced auto-save (PRD §5).
 * Returns the working draft, a patch function, and the save state.
 */
export function useProfileForm() {
  const { profile, session, refresh } = useAuth();
  const [draft, setDraft] = useState<Profile | null>(profile);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync when the canonical profile first loads.
  useEffect(() => {
    if (profile && !draft) setDraft(profile);
  }, [profile, draft]);

  const flush = useCallback(
    async (next: Profile) => {
      if (!session?.user) return;
      setSaveState("saving");
      const { id, role, created_at, updated_at, ...writable } = next;
      void id;
      void role;
      void created_at;
      void updated_at;
      const { error } = await supabase
        .from("profiles")
        .update(writable)
        .eq("id", session.user.id);
      if (error) {
        setSaveState("error");
      } else {
        setSaveState("saved");
        await refresh();
        setTimeout(() => setSaveState("idle"), 1500);
      }
    },
    [session, refresh],
  );

  const patch = useCallback(
    (changes: Partial<Profile>) => {
      setDraft((cur) => {
        if (!cur) return cur;
        const next = { ...cur, ...changes };
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => flush(next), 700);
        return next;
      });
    },
    [flush],
  );

  return { draft, patch, saveState };
}
